import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Modal } from 'react-bootstrap';
import { StandardButton } from '../Button';

// The ModalDeleteConfirm component is wrapped in forwardRef to allow passing a ref from the parent component.
const ModalConfirm = forwardRef(({ onConfirm, onClose, title }, ref) => {

  const DEFAULT_TEXT = 'Are you sure?';
  // Local state to manage modal visibility and item details
  const [isOpen, setIsOpen] = useState(false);
  const [currentItemId, setCurrentItemId] = useState(null);
  const [text, setText] = useState(DEFAULT_TEXT);

  // useImperativeHandle allows parent components to call `openModal` to control modal visibility.
  useImperativeHandle(ref, () => ({
    openModal: (data) => {
      setIsOpen(true); // Open the modal
      if (data.id) setCurrentItemId(data.id); // Set item ID
      if (data.text) setText(data.text) // Set item name
    },
  }));

  // Effect hook to manage overflow style on the body element when the modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'; // Disable scrolling when modal is open
    } else {
      document.body.style.overflow = ''; // Restore scrolling when modal is closed
    }

    // Cleanup function to reset the overflow style when the component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]); // Runs whenever `isOpen` changes

  // Function to close the modal and reset item details
  const closeModal = () => {
    setIsOpen(false); // Close the modal
    setCurrentItemId(null); // Clear the current item ID
    setText(DEFAULT_TEXT);
    if (onClose) onClose(currentItemId); // Call the onClose callback passed from the parent, passing the current item ID
  };

  return (
    <Modal
      show={isOpen} // Controls modal visibility based on isOpen state
      onHide={closeModal} // Close modal on hide (clicking the backdrop or close button)
      backdrop="static" // Prevent closing by clicking on the backdrop
      keyboard={false} // Disable closing by pressing the Escape key
      centered // Center the modal on the screen
      style={{ position: 'absolute', zIndex: '10000' }} // Custom styling for modal positioning
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{text}</Modal.Body>
      <Modal.Footer>
        {/* Close button that triggers closeModal */}
        <StandardButton outlined onClick={closeModal}>
          Close
        </StandardButton>
        {/* Submit button that triggers the onConfirm callback passed from the parent */}
        <StandardButton
          onClick={() => {
            if (onConfirm) onConfirm(currentItemId); // Call the onConfirm callback with the current item ID
            closeModal(); // Close the modal after confirmation
          }}
        >
          Submit
        </StandardButton>
      </Modal.Footer>
    </Modal>
  );
});

export default ModalConfirm;
